import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { actions } from '@ehi/global-marketing-interface';
import { setVehicleGridData } from 'actions/vehicleGrid';
import vehicleGridReducer from 'reducers/vehicleGrid';
import utils from 'utils';
import { combineReducers } from '@ehi/global-marketing-interface/redux';
import { VehicleGridView } from 'components/VehicleGridView';
import { filters as vehicleSelectFilters } from 'components/ReservationFlow/VehicleSelect/vehicleSelectFiltersConfig';

export const recursiveFind = (object, type) => {
  let desiredProps = null;
  if (object[':type'] === type) {
    // We found the object
    const modelProps = Object.getOwnPropertyNames(object).reduce((acc, curr) => {
      acc[curr] = object[curr];
      return acc;
    }, {});
    return { ...modelProps, itemKey: object.nodeName };
  }

  // Fix due to the cheerios container having an additional nested layer
  let nextContainer = object;
  if (object?.wcmCoreContainer) {
    nextContainer = object.wcmCoreContainer;
  }

  // Object not found, keep looking
  if (nextContainer?.[':items']) {
    Object.keys(nextContainer[':items']).forEach((itemKey) => {
      const found = recursiveFind(nextContainer[':items'][itemKey], type);
      if (found !== null) {
        desiredProps = { ...found, itemKey };
      }
    });
  }
  return desiredProps;
};

function compileFilters(vehicleFilters = []) {
  return vehicleFilters.map(({ filterCode, filterDescription, filterName }) => ({
    category: filterDescription || filterName || filterCode,
    keyPath: `filters.${filterCode}.filterCode`,
  }));
}

function initVehiclePageGridFilters(config = {}) {
  const { model = {} } = config;
  const {
    vehicleFilters = [],
    vehicles = [],
    vehicleTypes: categories = [],
    vehicleLandingPage: isVehicleLandingPage = false,
    ctaLink = {},
  } = recursiveFind(model, 'aweb/components/content/vehicles/vehicle-grid');

  return (dispatch) => {
    let filters = [];
    let labelFn = (l) => l;
    if (utils.gmi.isArrayNotEmpty(vehicleFilters)) {
      vehicleFilters.sort((a, b) =>
        utils.array.sortArray(
          a,
          b,
          'filterCode',
          vehicleSelectFilters.map((item) => item.category)
        )
      );
      labelFn = (code, { keyPath }) => {
        const matchingFilter = vehicleFilters.find((filter) => keyPath.includes(filter.filterCode));
        const matchingValue = matchingFilter?.filterValues?.find?.((filterValue) => filterValue.code === code);
        return matchingValue?.description ?? code;
      };
      filters = compileFilters(vehicleFilters);
    }
    dispatch(setVehicleGridData({ isVehicleLandingPage, categories, filterExpanded: false, ctaLink, vehicleFilters }));
    dispatch(
      actions.composeFiltersUiShape({
        items: vehicles,
        storeKey: 'vehicle-page__grid',
        filters,
        labelFn,
        segments: [],
      })
    );
  };
}

/**
 * A public init method must exist to be called from uiManager
 * @param {Object} store The redux store for the site
 * @param {HTMLElement} domEl The dom element related to this module
 */
export default {
  init(store, domEl, model) {
    combineReducers(vehicleGridReducer.reducer);
    const vehiclePageModel = model[':items'].root;
    if (vehiclePageModel) {
      store.dispatch(
        initVehiclePageGridFilters({
          model: vehiclePageModel,
        })
      );

      const title = recursiveFind(vehiclePageModel, 'aweb/components/content/title');
      const countrySelector = recursiveFind(vehiclePageModel, 'aweb/components/content/country-dropdown');
      const {locationCode} = model;

      if (domEl) {
        const root = createRoot(domEl);
        root.render(
          <Provider store={store}>
            <VehicleGridView title={title} countrySelector={countrySelector} locationCode={locationCode} />
          </Provider>
        );
      }
    }
  },
};
